import { mutationOptions, type QueryClient } from '@tanstack/react-query';

import { createAggregatedReportFolder } from './create-aggregated-report-folder';
import { createReport } from './create-report';
import { deleteAggregatedReportFolder } from './delete-aggregated-report-folder';
import { deleteReport } from './delete-report';
import { moveAggregatedReportsToFolder } from './move-aggregated-reports-to-folder';
import { reportQueryKeys } from './report.queries';
import { updateAggregatedReportFolder } from './update-aggregated-report-folder';
import { updateReport } from './update-report';

async function invalidateReportQueries(queryClient: QueryClient) {
  await queryClient.invalidateQueries({
    queryKey: reportQueryKeys.aggregatedLists(),
  });
}

async function invalidateReportFolderQueries(queryClient: QueryClient) {
  await queryClient.invalidateQueries({
    queryKey: reportQueryKeys.aggregatedFolderLists(),
  });
}

export const createReportMutationOptions = (queryClient: QueryClient) =>
  mutationOptions({
    mutationFn: createReport,
    onSuccess: async () => {
      await invalidateReportQueries(queryClient);
    },
  });

export const updateReportMutationOptions = (queryClient: QueryClient) =>
  mutationOptions({
    mutationFn: updateReport,
    onSuccess: async () => {
      await invalidateReportQueries(queryClient);
    },
  });

export const deleteReportMutationOptions = (queryClient: QueryClient) =>
  mutationOptions({
    mutationFn: deleteReport,
    onSuccess: async () => {
      await invalidateReportQueries(queryClient);
    },
  });

export const createAggregatedReportFolderMutationOptions = (
  queryClient: QueryClient,
) =>
  mutationOptions({
    mutationFn: createAggregatedReportFolder,
    onSuccess: async () => {
      await invalidateReportFolderQueries(queryClient);
    },
  });

export const updateAggregatedReportFolderMutationOptions = (
  queryClient: QueryClient,
) =>
  mutationOptions({
    mutationFn: updateAggregatedReportFolder,
    onSuccess: async () => {
      await invalidateReportFolderQueries(queryClient);
    },
  });

export const deleteAggregatedReportFolderMutationOptions = (
  queryClient: QueryClient,
) =>
  mutationOptions({
    mutationFn: deleteAggregatedReportFolder,
    onSuccess: async () => {
      await Promise.all([
        invalidateReportFolderQueries(queryClient),
        invalidateReportQueries(queryClient),
      ]);
    },
  });

export const moveAggregatedReportsToFolderMutationOptions = (
  queryClient: QueryClient,
) =>
  mutationOptions({
    mutationFn: moveAggregatedReportsToFolder,
    onSuccess: async () => {
      await invalidateReportQueries(queryClient);
    },
  });
